import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import AppToast from './AppToast';
import { AttendanceBoardTimeAlerts } from './AttendanceBoardTimeAlerts';
import { AttendanceCheckOutTime } from './AttendanceCheckOutTime';
import { attendanceAPI, managementAPI } from '../services/api';
import {
  DAY_COMPLETION_LABEL,
  SUPERVISOR_TODAY_STATUS_LABEL,
  dayCompletionBadgeClass,
  resolveDayCompletion,
  supervisorTodayStatusBadgeClass,
} from '../utils/supervisorTodayStatus';
import { formatLocalDateYmd } from '../utils/localCalendarDate';
import { formatAttendanceHm, parseApiDateTime, parseApiDateTimeMs } from '../utils/attendanceTime';

const WEEKDAYS = [
  { id: 0, label: 'Lun' },
  { id: 1, label: 'Mar' },
  { id: 2, label: 'Mie' },
  { id: 3, label: 'Jue' },
  { id: 4, label: 'Vie' },
  { id: 5, label: 'Sab' },
  { id: 6, label: 'Dom' },
];

const FactorySelector = ({ factories, selectedFactoryId, setSelectedFactoryId }) => {
  const selectedFactory = useMemo(
    () => factories.find((f) => String(f.id) === selectedFactoryId),
    [factories, selectedFactoryId]
  );
  return (
    <div className="card-mobile">
      <select
        className="input-mobile"
        aria-label="Elegí sede o fábrica"
        value={selectedFactoryId}
        onChange={(e) => setSelectedFactoryId(e.target.value)}
      >
        <option value="">Seleccionar...</option>
        {factories.map((f) => (
          <option key={f.id} value={f.id}>{f.name}</option>
        ))}
      </select>
      {selectedFactory && (
        <p className="text-xs text-gray-500 mt-2">
          {selectedFactory.address} · radio {selectedFactory.radius_meters}m
        </p>
      )}
    </div>
  );
};

const AttendanceEmployee = ({ factoryIdNum, loading, setLoading, setErr, setMsg, onAfterPunch }) => {
  const [activeCheckInAt, setActiveCheckInAt] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [statusLoading, setStatusLoading] = useState(false);
  const [distanceAlert, setDistanceAlert] = useState('');

  const refreshPunchStatus = async () => {
    if (!factoryIdNum) {
      setActiveCheckInAt(null);
      setElapsedMs(0);
      return;
    }
    try {
      setStatusLoading(true);
      const res = await attendanceAPI.punchStatus(factoryIdNum);
      const checkInTime = res?.data?.check_in_time ? parseApiDateTimeMs(res.data.check_in_time) : null;
      if (res?.data?.is_checked_in && checkInTime && Number.isFinite(checkInTime)) {
        setActiveCheckInAt(checkInTime);
        setElapsedMs(Math.max(0, Date.now() - checkInTime));
      } else {
        setActiveCheckInAt(null);
        setElapsedMs(0);
      }
    } catch {
      setActiveCheckInAt(null);
      setElapsedMs(0);
    } finally {
      setStatusLoading(false);
    }
  };

  useEffect(() => {
    refreshPunchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [factoryIdNum]);

  useEffect(() => {
    if (!activeCheckInAt) return undefined;
    const tick = () => setElapsedMs(Math.max(0, Date.now() - activeCheckInAt));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [activeCheckInAt]);

  const formatElapsed = (ms) => {
    const totalMinutes = Math.floor(ms / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${String(hours).padStart(2, '0')}h:${String(minutes).padStart(2, '0')}m`;
  };

  const getCurrentLocation = () =>
    new Promise((resolve, reject) => {
      if (!navigator.geolocation) return reject(new Error('Geolocation not supported'));
      navigator.geolocation.getCurrentPosition(
        (position) => resolve({ lat: position.coords.latitude, lng: position.coords.longitude }),
        (error) => reject(error),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    });

  const doPunch = async (type) => {
    if (!factoryIdNum) return;
    setErr('');
    setMsg('');
    try {
      setLoading(true);
      const loc = await getCurrentLocation();
      if (type === 'in') {
        await attendanceAPI.checkIn(factoryIdNum, loc.lat, loc.lng);
      } else {
        await attendanceAPI.checkOut(factoryIdNum, loc.lat, loc.lng);
      }
      await refreshPunchStatus();
      if (onAfterPunch) await onAfterPunch();
    } catch (e) {
      const detail = e?.response?.data?.detail || '';
      const distanceMatch = typeof detail === 'string' && detail.match(/You are\s+(\d+(?:\.\d+)?)m away/i);
      const radiusMatch = typeof detail === 'string' && detail.match(/within\s+(\d+(?:\.\d+)?)m/i);
      if (distanceMatch && radiusMatch) {
        const distanceMeters = Number(distanceMatch[1]);
        const radiusMeters = Number(radiusMatch[1]);
        setDistanceAlert(
          `Estás a ${distanceMeters.toFixed(1)} m de la sede. Para registrar ${
            type === 'in' ? 'el check-in' : 'el check-out'
          } tenés que estar dentro de ${radiusMeters.toFixed(0)} m.`
        );
      } else {
        setErr(detail || 'No se pudo registrar la fichada');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card-mobile space-y-2">
      {activeCheckInAt ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2">
          <p className="text-xs text-emerald-700">Tiempo trabajado desde check-in</p>
          <p className="text-2xl font-semibold text-emerald-800 tabular-nums">{formatElapsed(elapsedMs)}</p>
        </div>
      ) : null}
      <button
        className={`btn-mobile w-full ${activeCheckInAt ? 'btn-danger' : 'btn-success'}`}
        onClick={() => doPunch(activeCheckInAt ? 'out' : 'in')}
        disabled={loading || statusLoading || !factoryIdNum}
      >
        {activeCheckInAt ? 'Check-out' : 'Check-in'}
      </button>
      {distanceAlert && (
        <div className="modal-overlay" onClick={() => setDistanceAlert('')}>
          <div className="modal-mobile" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="text-lg font-semibold text-gray-900">No estás dentro del radio permitido</h3>
              <button type="button" onClick={() => setDistanceAlert('')} className="text-2xl leading-none">×</button>
            </div>
            <div className="modal-content">
              <p className="text-sm text-slate-700 leading-relaxed">{distanceAlert}</p>
              <button
                type="button"
                className="btn-mobile btn-primary w-full mt-4"
                onClick={() => setDistanceAlert('')}
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AttendanceOwner = ({
  factoryIdNum,
  loading,
  setLoading,
  setErr,
  setMsg,
  selectedCompanyId,
  onCompanyCreated,
  loadBase,
  employees,
  shifts,
  refreshFactoryData,
}) => {
  const [factoryName, setFactoryName] = useState('');
  const [factoryAddress, setFactoryAddress] = useState('');
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [radius, setRadius] = useState('100');
  const [shiftName, setShiftName] = useState('Turno Manana');
  const [startTime, setStartTime] = useState('08:00');
  const [endTime, setEndTime] = useState('17:00');
  const [grace, setGrace] = useState('10');
  const [weekdays, setWeekdays] = useState([0, 1, 2, 3, 4]);
  const [employeeUserId, setEmployeeUserId] = useState('');
  const [selectedShiftId, setSelectedShiftId] = useState('');
  const [exportYear, setExportYear] = useState(String(new Date().getFullYear()));
  const [exportMonth, setExportMonth] = useState(String(new Date().getMonth() + 1));

  const toggleWeekday = (id) => {
    setWeekdays((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const createFactory = async () => {
    const companyId = selectedCompanyId;
    if (!companyId) return setErr('Crea una empresa primero');
    try {
      await managementAPI.createFactory({
        company_id: companyId,
        name: factoryName || 'Nueva Planta',
        address: factoryAddress || 'Dirección pendiente',
        latitude: Number(lat || 0),
        longitude: Number(lng || 0),
        radius_meters: Number(radius || 100),
      });
      setFactoryName('');
      setFactoryAddress('');
      setLat('');
      setLng('');
      await loadBase();
      if (onCompanyCreated) await onCompanyCreated();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'No se pudo crear fábrica');
    }
  };

  const createShift = async () => {
    if (!factoryIdNum) return;
    if (!weekdays.length) return setErr('Selecciona al menos un día');
    try {
      for (const wd of weekdays) {
        // eslint-disable-next-line no-await-in-loop
        await managementAPI.createShift({
          factory_id: factoryIdNum,
          name: shiftName,
          weekday: wd,
          start_time: startTime,
          end_time: endTime,
          grace_minutes: Number(grace),
          active: true,
        });
      }
      await refreshFactoryData();
      setMsg('Turnos creados');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'No se pudo crear turnos');
    }
  };

  const assignEmployeeFactoryAndShift = async () => {
    if (!factoryIdNum || !employeeUserId) return;
    try {
      const feRes = await managementAPI.assignFactoryEmployee({
        factory_id: factoryIdNum,
        user_id: Number(employeeUserId),
        is_supervisor: false,
        active: true,
      });
      const shiftId = selectedShiftId ? Number(selectedShiftId) : shifts[0]?.id;
      if (shiftId) {
        await managementAPI.assignEmployeeShift({
          factory_employee_id: feRes.data.id,
          shift_id: shiftId,
          effective_from: formatLocalDateYmd(),
        });
      }
      setEmployeeUserId('');
      await refreshFactoryData();
      setMsg('Empleado asignado');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'No se pudo asignar empleado');
    }
  };

  const exportMonthly = async () => {
    if (!factoryIdNum) return;
    try {
      const res = await attendanceAPI.monthlyExport(factoryIdNum, Number(exportYear), Number(exportMonth));
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.setAttribute('download', `fichadas_${exportYear}_${String(exportMonth).padStart(2, '0')}.csv`);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'No se pudo exportar');
    }
  };

  return (
    <>
      <div className="card-mobile space-y-2">
        <h3 className="font-semibold">Crear sede</h3>
        <input className="input-mobile" placeholder="Nombre fábrica" value={factoryName} onChange={(e) => setFactoryName(e.target.value)} />
        <input className="input-mobile" placeholder="Dirección" value={factoryAddress} onChange={(e) => setFactoryAddress(e.target.value)} />
        <div className="grid grid-cols-3 gap-2">
          <input className="input-mobile" placeholder="Lat" value={lat} onChange={(e) => setLat(e.target.value)} />
          <input className="input-mobile" placeholder="Lng" value={lng} onChange={(e) => setLng(e.target.value)} />
          <input className="input-mobile" placeholder="Radio" value={radius} onChange={(e) => setRadius(e.target.value)} />
        </div>
        <button className="btn-mobile btn-primary" onClick={createFactory}>Crear fábrica</button>
      </div>
      <div className="card-mobile space-y-2">
        <h3 className="font-semibold">Crear turno semanal</h3>
        <input className="input-mobile" value={shiftName} onChange={(e) => setShiftName(e.target.value)} />
        <div className="grid grid-cols-3 gap-2">
          <input className="input-mobile" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
          <input className="input-mobile" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
          <input className="input-mobile" type="number" min="0" max="180" value={grace} onChange={(e) => setGrace(e.target.value)} />
        </div>
        <div className="flex flex-wrap gap-2">
          {WEEKDAYS.map((d) => (
            <button
              key={d.id}
              type="button"
              className={`btn-mobile text-xs py-1 px-2 min-h-0 ${weekdays.includes(d.id) ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => toggleWeekday(d.id)}
            >
              {d.label}
            </button>
          ))}
        </div>
        <button className="btn-mobile btn-primary" onClick={createShift} disabled={!factoryIdNum}>Crear turnos</button>
      </div>
      <div className="card-mobile space-y-2">
        <h3 className="font-semibold">Asignar empleado</h3>
        <select className="input-mobile" value={employeeUserId} onChange={(e) => setEmployeeUserId(e.target.value)}>
          <option value="">Empleado...</option>
          {employees.map((e) => (<option key={e.id} value={e.id}>{e.name} ({e.email})</option>))}
        </select>
        <select className="input-mobile" value={selectedShiftId} onChange={(e) => setSelectedShiftId(e.target.value)}>
          <option value="">Turno (opcional: usa primero)</option>
          {shifts.map((s) => (<option key={s.id} value={s.id}>{s.name} - {s.weekday} {s.start_time}-{s.end_time}</option>))}
        </select>
        <button className="btn-mobile btn-primary" onClick={assignEmployeeFactoryAndShift} disabled={!factoryIdNum || !employeeUserId}>Asignar</button>
      </div>
      <div className="card-mobile">
        <div className="grid grid-cols-3 gap-2 mb-2">
          <input className="input-mobile" type="number" value={exportYear} onChange={(e) => setExportYear(e.target.value)} />
          <input className="input-mobile" type="number" min="1" max="12" value={exportMonth} onChange={(e) => setExportMonth(e.target.value)} />
          <button className="btn-mobile btn-secondary" onClick={exportMonthly} disabled={!factoryIdNum}>Export CSV</button>
        </div>
      </div>
    </>
  );
};

const formatTimeHm = (d) =>
  `${d.getHours()} hs ${String(d.getMinutes()).padStart(2, '0')} min`;

const formatSessionDate = (d) =>
  d.toLocaleDateString('es-AR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

const sessionWorkedMs = (s) => {
  if (!s.check_in_time) return 0;
  const start = parseApiDateTimeMs(s.check_in_time);
  if (!Number.isFinite(start)) return 0;
  const end = s.check_out_time ? parseApiDateTimeMs(s.check_out_time) : Date.now();
  if (!Number.isFinite(end)) return 0;
  return Math.max(0, end - start);
};

const formatTotalWorked = (ms) => {
  if (ms < 0 || !Number.isFinite(ms)) return '0 min';
  const totalM = Math.floor(ms / 60000);
  const h = Math.floor(totalM / 60);
  const m = totalM % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m} min`;
};

const RecentSessionCard = ({ session, index }) => {
  const inDate = session.check_in_time ? parseApiDateTime(session.check_in_time) : null;
  const outDate = session.check_out_time ? parseApiDateTime(session.check_out_time) : null;
  const worked = sessionWorkedMs(session);
  const hasOut = Boolean(session.check_out_time);

  if (!inDate) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-600">
        Registro #{index + 1}: sin hora de ingreso
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-5 shadow-sm">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 sm:gap-x-8">
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-emerald-800">Ingreso</p>
          <p className="text-sm font-medium capitalize leading-snug text-slate-800">{formatSessionDate(inDate)}</p>
          <p className="text-xl font-semibold tabular-nums text-slate-900">{formatTimeHm(inDate)}</p>
        </div>
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">Salida</p>
          {hasOut && outDate ? (
            <>
              <p className="text-sm font-medium capitalize leading-snug text-slate-800">{formatSessionDate(outDate)}</p>
              <p className="text-xl font-semibold tabular-nums text-slate-900">{formatTimeHm(outDate)}</p>
            </>
          ) : (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-sm font-medium text-amber-900 leading-relaxed">
              Pendiente — aún no registraste el egreso en esta jornada.
            </p>
          )}
        </div>
      </div>
      <div className="mt-5 flex flex-col gap-1 rounded-xl bg-slate-100 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <span className="text-sm text-slate-600">Tiempo trabajado en esta jornada</span>
        <span className="text-lg font-bold tabular-nums text-slate-900">{formatTotalWorked(worked)}</span>
      </div>
    </div>
  );
};

const FactoryAttendance = ({ selectedCompanyId, onCompanyCreated }) => {
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';
  const [factories, setFactories] = useState([]);
  const [selectedFactoryId, setSelectedFactoryId] = useState('');
  const [todayRows, setTodayRows] = useState([]);
  const [recentSessions, setRecentSessions] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [factoryEmployees, setFactoryEmployees] = useState([]);
  const [boardDate, setBoardDate] = useState(() => formatLocalDateYmd());
  const [boardRowAlertKey, setBoardRowAlertKey] = useState(null);
  const [msg, setMsg] = useState('');
  const [totalWorkMs, setTotalWorkMs] = useState(0);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const factoryIdNum = selectedFactoryId ? Number(selectedFactoryId) : null;
  const selectedFactory = useMemo(
    () => factories.find((f) => String(f.id) === String(selectedFactoryId)),
    [factories, selectedFactoryId]
  );
  const isSupervisorThisFactory = Boolean(selectedFactory?.is_supervisor);

  const loadBase = async () => {
    const fRes = await attendanceAPI.myFactories();
    const allFactories = fRes.data || [];
    const filteredFactories = selectedCompanyId
      ? allFactories.filter((f) => String(f.company_id) === String(selectedCompanyId))
      : allFactories;
    setFactories(filteredFactories);
    const preferredCheckoutFactoryId = localStorage.getItem('preferred_checkout_factory_id');
    if (preferredCheckoutFactoryId) {
      const preferredExists = filteredFactories.some((f) => String(f.id) === String(preferredCheckoutFactoryId));
      if (preferredExists) {
        setSelectedFactoryId(String(preferredCheckoutFactoryId));
      } else if (!selectedFactoryId && filteredFactories?.length) {
        setSelectedFactoryId(String(filteredFactories[0].id));
      }
      localStorage.removeItem('preferred_checkout_factory_id');
    } else if (!selectedFactoryId && filteredFactories?.length) {
      setSelectedFactoryId(String(filteredFactories[0].id));
    }
    if (isOwner) {
      const [eRes] = await Promise.all([managementAPI.employeesDirectory()]);
      setEmployees(eRes.data || []);
    }
  };

  const refreshFactoryData = useCallback(async () => {
    if (!factoryIdNum) {
      setTodayRows([]);
      setRecentSessions([]);
      return;
    }
    if (isOwner) {
      const companyIdForShifts = selectedFactory?.company_id;
      const [todayRes, shiftRes] = await Promise.all([
        attendanceAPI.supervisorToday(factoryIdNum, boardDate),
        companyIdForShifts
          ? managementAPI.listShifts({ company_id: companyIdForShifts })
          : Promise.resolve({ data: [] }),
      ]);
      setTodayRows(todayRes.data || []);
      setShifts(shiftRes.data || []);
      const feRes = await managementAPI.factoryEmployees(factoryIdNum);
      setFactoryEmployees(feRes.data || []);
      setRecentSessions([]);
      return;
    }
    if (isSupervisorThisFactory) {
      setTodayRows([]);
      setRecentSessions([]);
    } else {
      setTodayRows([]);
      const rec = await attendanceAPI.recentPunchSessions(factoryIdNum, 10);
      setRecentSessions((rec.data || []).slice(0, 10));
    }
  }, [factoryIdNum, isOwner, isSupervisorThisFactory, boardDate, selectedFactory?.company_id]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await loadBase();
      } catch (e) {
        setErr(e?.response?.data?.detail || 'No se pudo cargar fichada');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId]);

  useEffect(() => {
    (async () => {
      try {
        setErr('');
        await refreshFactoryData();
      } catch {
        setTodayRows([]);
        setRecentSessions([]);
      }
    })();
  }, [refreshFactoryData]);

  useEffect(() => {
    setBoardRowAlertKey(null);
  }, [boardDate]);

  useEffect(() => {
    const compute = () => {
      const now = Date.now();
      setTotalWorkMs(
        recentSessions.reduce((acc, s) => {
          if (!s.check_in_time) return acc;
          const start = parseApiDateTimeMs(s.check_in_time);
          if (!Number.isFinite(start)) return acc;
          const end = s.check_out_time ? parseApiDateTimeMs(s.check_out_time) : now;
          if (!Number.isFinite(end)) return acc;
          return acc + Math.max(0, end - start);
        }, 0)
      );
    };

    compute();
    const hasOpen = recentSessions.some((s) => s.check_in_time && !s.check_out_time);
    if (!hasOpen) return undefined;
    const id = setInterval(compute, 1000);
    return () => clearInterval(id);
  }, [recentSessions]);

  return (
    <div
      className={`space-y-4${!isOwner ? ' fichada-stack--employee' : ''}`}
    >
      {isOwner ? (
        <div className="card-mobile">
          <h2 className="text-lg font-semibold text-gray-900">Fichada (Owner)</h2>
        </div>
      ) : null}

      <FactorySelector factories={factories} selectedFactoryId={selectedFactoryId} setSelectedFactoryId={setSelectedFactoryId} />

      {isOwner ? (
        <AttendanceOwner
          factoryIdNum={factoryIdNum}
          loading={loading}
          setLoading={setLoading}
          setErr={setErr}
          setMsg={setMsg}
          selectedCompanyId={selectedCompanyId}
          onCompanyCreated={onCompanyCreated}
          loadBase={loadBase}
          employees={employees}
          shifts={shifts}
          refreshFactoryData={refreshFactoryData}
        />
      ) : (
        <AttendanceEmployee
          factoryIdNum={factoryIdNum}
          loading={loading}
          setLoading={setLoading}
          setErr={setErr}
          setMsg={setMsg}
          onAfterPunch={refreshFactoryData}
        />
      )}

      {!isOwner && !isSupervisorThisFactory && (
        <div className="card-mobile">
          <h3 className="font-semibold">Últimas fichadas</h3>
          <div className="mt-5 space-y-5">
            {recentSessions.length === 0 && <p className="text-sm text-gray-500">Sin fichadas registradas aún.</p>}
            {recentSessions.map((s, idx) => (
              <RecentSessionCard key={`${s.check_in_time}-${idx}`} session={s} index={idx} />
            ))}
            {recentSessions.length > 0 && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-4">
                <p className="text-sm font-semibold text-slate-800">
                  Total acumulado en este listado:{' '}
                  <span className="tabular-nums">{formatTotalWorked(totalWorkMs)}</span>
                </p>
                <p className="mt-2 text-xs text-slate-500 leading-relaxed">
                  Suma de las duraciones de cada jornada mostrada arriba (Si hay una abierta sin egreso, se cuenta hasta
                  ahora).
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {isOwner && (
        <div className="card-mobile">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-semibold">Tablero</h3>
            <input className="input-mobile max-w-[11rem]" type="date" value={boardDate} onChange={(e) => setBoardDate(e.target.value)} />
          </div>
          <div className="space-y-2 mt-3">
            {todayRows.length === 0 && <p className="text-sm text-gray-500">Sin datos.</p>}
            {todayRows.map((r) => {
              const jc = resolveDayCompletion(r, boardDate);
              return (
              <div key={r.employee_user_id} className="list-item-mobile block">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-semibold text-gray-900">{r.employee_name}</p>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    {jc !== 'none' && (
                      <span className={`status-badge ${dayCompletionBadgeClass(jc)}`}>
                        {DAY_COMPLETION_LABEL[jc] || jc}
                      </span>
                    )}
                    <span className={`status-badge ${supervisorTodayStatusBadgeClass(r.status)}`}>
                      {SUPERVISOR_TODAY_STATUS_LABEL[r.status] || r.status}
                    </span>
                  </div>
                </div>
                <div className="mt-1 space-y-1 text-xs text-gray-500">
                  <p>
                    Turno: {r.shift_name || 'N/A'}
                    {r.shift_start && r.shift_end ? ` (${r.shift_start}–${r.shift_end})` : ''}
                  </p>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
                    <span className="inline-flex items-center gap-1.5 tabular-nums">
                      <span className="text-gray-600">In:</span>
                      {formatAttendanceHm(r.check_in_time)}
                      <AttendanceBoardTimeAlerts
                        row={r}
                        boardDateYmd={boardDate}
                        openKey={boardRowAlertKey}
                        setOpenKey={setBoardRowAlertKey}
                        rowKey={`own-${r.employee_user_id}`}
                        showExit={false}
                      />
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="text-gray-600">Out:</span>
                      <AttendanceCheckOutTime
                        iso={r.check_out_time}
                        checkOutNextDay={r.check_out_next_day}
                      />
                      <AttendanceBoardTimeAlerts
                        row={r}
                        boardDateYmd={boardDate}
                        openKey={boardRowAlertKey}
                        setOpenKey={setBoardRowAlertKey}
                        rowKey={`own-${r.employee_user_id}`}
                        showEntry={false}
                      />
                    </span>
                  </div>
                </div>
              </div>
            );
            })}
          </div>
          {factoryEmployees.length > 0 && <p className="text-xs text-gray-500 mt-2">Asignados a fábrica: {factoryEmployees.length}</p>}
        </div>
      )}
      <AppToast
        message={err || msg}
        variant={err ? 'error' : 'success'}
        onClose={() => {
          setErr('');
          setMsg('');
        }}
      />
    </div>
  );
};

export default FactoryAttendance;
